import React, { useRef } from 'react';
import { gsap, useGSAP } from '../../lib/gsap';

/** A trailing ring that swells over interactive elements. Fine pointers only. */
const Cursor = () => {
   const ring = useRef<HTMLDivElement>(null);
   const dot = useRef<HTMLDivElement>(null);

   useGSAP(() => {
      if (!window.matchMedia('(pointer: fine)').matches) return;
      if (!ring.current || !dot.current) return;

      gsap.set([ring.current, dot.current], { xPercent: -50, yPercent: -50, autoAlpha: 0 });
      const rx = gsap.quickTo(ring.current, 'x', { duration: 0.55, ease: 'power3.out' });
      const ry = gsap.quickTo(ring.current, 'y', { duration: 0.55, ease: 'power3.out' });
      const dx = gsap.quickTo(dot.current, 'x', { duration: 0.12, ease: 'power3.out' });
      const dy = gsap.quickTo(dot.current, 'y', { duration: 0.12, ease: 'power3.out' });
      let shown = false;
      let hovering = false;

      const move = (e: PointerEvent) => {
         if (!shown) {
            shown = true;
            gsap.to([ring.current, dot.current], { autoAlpha: 1, duration: 0.4 });
         }
         rx(e.clientX);
         ry(e.clientY);
         dx(e.clientX);
         dy(e.clientY);

         const interactive = !!(e.target as Element)?.closest?.('a, button, [role="button"], input, textarea, label');
         if (interactive !== hovering) {
            hovering = interactive;
            gsap.to(ring.current, {
               scale: interactive ? 1.9 : 1,
               borderColor: interactive ? 'rgba(255,42,61,0.9)' : 'rgba(236,230,220,0.35)',
               duration: 0.45,
            });
            gsap.to(dot.current, { scale: interactive ? 0 : 1, duration: 0.3 });
         }
      };
      const leave = () => {
         shown = false;
         gsap.to([ring.current, dot.current], { autoAlpha: 0, duration: 0.3 });
      };

      window.addEventListener('pointermove', move, { passive: true });
      document.documentElement.addEventListener('pointerleave', leave);
      return () => {
         window.removeEventListener('pointermove', move);
         document.documentElement.removeEventListener('pointerleave', leave);
      };
   });

   return (
      <>
         <div
            ref={ring}
            aria-hidden="true"
            className="pointer-events-none invisible fixed left-0 top-0 z-[100] h-9 w-9 rounded-full border border-bone/35 mix-blend-difference"
         />
         <div
            ref={dot}
            aria-hidden="true"
            className="pointer-events-none invisible fixed left-0 top-0 z-[100] h-1.5 w-1.5 rounded-full bg-signal"
         />
      </>
   );
};

export default Cursor;
